// The slot names' own module, not the organizations folder's index, which would pull its chooser into the main bundle.
import { organizationDetailsSlot } from '@/features/site-management/organizations/organization'
import { organizationFormSectionsSlot } from '@/features/site-management/organizations/paths'
import { detailsSlots } from '@/shared/details'
import type { Feature } from '@/shared/registry'
import { Puzzle } from '@/shared/ui/icons'

import {
  apiClientRoute,
  apiClientsPath,
  apiDocumentationHref,
  createApiClientPath,
  editApiClientRoute,
  readApiClientsPermission,
  writeApiClientsPermission,
} from './api-clients/paths'
import { ApiClientNamesProvider } from './api-clients/ApiClientNamesProvider'
import {
  createWebhookPath,
  editWebhookRoute,
  readWebhooksPermission,
  webhookRoute,
  webhooksPath,
} from './webhooks/paths'

/** Channels: API clients and webhooks. Its screens, and its contributions to other areas' slots, register here. */
export const feature: Feature = {
  id: 'channels',
  // The name lookup for the `ApiClient` actor type on "Created by" lines (CHN-04).
  provider: ApiClientNamesProvider,
  routes: [
    // Find, Details, Create and Edit Webhook (CHN-08).
    {
      path: webhooksPath,
      titleId: 'channels.webhooks.find.title',
      page: () => import('./webhooks/FindWebhooksPage').then((module) => module.FindWebhooksPage),
    },
    {
      path: createWebhookPath,
      titleId: 'channels.webhooks.create.title',
      page: () => import('./webhooks/WebhookFormPages').then((module) => module.CreateWebhookPage),
    },
    {
      path: webhookRoute,
      titleId: 'channels.webhooks.details.title',
      page: () => import('./webhooks/WebhookDetailsPage').then((module) => module.WebhookDetailsPage),
    },
    {
      path: editWebhookRoute,
      titleId: 'channels.webhooks.edit.title',
      page: () => import('./webhooks/WebhookFormPages').then((module) => module.EditWebhookPage),
    },
    // Find, Details, Create and Edit API Client (CHN-04).
    {
      path: apiClientsPath,
      titleId: 'channels.apiClients.find.title',
      page: () => import('./api-clients/FindApiClientsPage').then((module) => module.FindApiClientsPage),
    },
    {
      path: createApiClientPath,
      titleId: 'channels.apiClients.create.title',
      page: () => import('./api-clients/ApiClientFormPages').then((module) => module.CreateApiClientPage),
    },
    {
      path: apiClientRoute,
      titleId: 'channels.apiClients.details.title',
      page: () => import('./api-clients/ApiClientDetailsPage').then((module) => module.ApiClientDetailsPage),
    },
    {
      path: editApiClientRoute,
      titleId: 'channels.apiClients.edit.title',
      page: () => import('./api-clients/ApiClientFormPages').then((module) => module.EditApiClientPage),
    },
  ],
  adminHome: {
    categories: [{ id: 'integrations', section: 'system', titleId: 'channels.adminHomeCategory', icon: Puzzle }],
    links: [
      { id: 'find-webhooks', category: 'integrations', labelId: 'channels.webhooks.find.title', path: webhooksPath, permission: readWebhooksPermission },
      { id: 'find-api-clients', category: 'integrations', labelId: 'channels.apiClients.find.title', path: apiClientsPath, permission: readApiClientsPermission },
      { id: 'create-api-client', category: 'integrations', labelId: 'channels.apiClients.create.title', path: createApiClientPath, permission: writeApiClientsPermission },
      // The Swagger UI, where the mockup puts it: System & Administration > Web Application. For those who manage API clients (110589).
      { id: 'api-documentation', category: 'web-application', labelId: 'channels.apiClients.apiDocumentation', href: apiDocumentationHref, permission: readApiClientsPermission },
    ],
  },
  slots: [
    {
      // The organization's webhooks, a section of Create and Edit Organization (SM-11's slot).
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'webhooks',
      labelId: 'channels.webhooks.organizationSection.title',
      component: () => import('./webhooks/OrganizationWebhooksSection').then((module) => module.OrganizationWebhooksSection),
    },
    {
      // The organization's API clients, a tab on Organization Details (SM-08's slot).
      kind: 'tab',
      slot: detailsSlots(organizationDetailsSlot).tabs,
      id: 'api-clients',
      labelId: 'channels.apiClients.organizationTab.tab',
      component: () => import('./api-clients/OrganizationApiClientsTab').then((module) => module.OrganizationApiClientsTab),
    },
  ],
}
